import { useCallback, useEffect, useMemo, useState } from 'react'
import DocumentsView from './DocumentsView'
import { SocialDocument } from '../../model/domain/social-document'
import { TaskSocialDocuments } from '../../ws/TaskSocialDocuments'
import { ShareCenterOption } from '../../model/domain/share-option'
import { User } from '../../model/domain/user'

function DocumentsContainer() {
    const [loadingSocialDocuments, setLoadingSocialDocuments] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [socialDocuments, setSocialDocuments] = useState<SocialDocument[]>([])
    const [search, setSearch] = useState('')
    const [loadingMore, setLoadingMore] = useState(false)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [showSaveModal, setShowSaveModal] = useState(false)
    const [selectedSocialDocument, setSelectedSocialDocument] =
        useState<SocialDocument | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [socialDocumentIdToDelete, setSocialDocumentIdToDelete] = useState<
        SocialDocument['id'] | null
    >(null)
    const [loadingDelete, setLoadingDelete] = useState(false)
    const [saveSuccess, setSaveSuccess] = useState(false)
    const [deleteSuccess, setDeleteSuccess] = useState(false)
    const [shareOptions, setShareOptions] = useState<
        ShareCenterOption[] | undefined
    >()
    const [loadingShareOptions, setLoadingShareOptions] = useState(false)
    const [user, setUser] = useState<User | null>()
    const [loadingUser, setLoadingUser] = useState(false)

    const task = useMemo(() => new TaskSocialDocuments(), [])

    const fetchUser = useCallback(async () => {
        try {
            setLoadingUser(true)
            const response = await task.getUser()
            setUser(response)
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message)
            }
        } finally {
            setLoadingUser(false)
        }
    }, [task])

    useEffect(() => {
        fetchUser()
    }, [fetchUser])

    const fetchShareOptions = useCallback(async () => {
        try {
            setLoadingShareOptions(true)
            const response = await task.getShareOptions()
            setShareOptions(response)
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message)
            }
        } finally {
            setLoadingShareOptions(false)
        }
    }, [task])

    useEffect(() => {
        fetchShareOptions()
    }, [fetchShareOptions])

    const fetchSocialDocuments = useCallback(
        async (page: number, isInitial: boolean) => {
            try {
                isInitial
                    ? setLoadingSocialDocuments(true)
                    : setLoadingMore(true)
                if (isInitial) {
                    setSocialDocuments([])
                }
                setError(null)
                const response = await task.getSocialDocuments(page)
                setSocialDocuments((socialDocuments) => [
                    ...socialDocuments,
                    ...response.items,
                ])
                setHasMore(response.currentPage < response.totalPages)
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message)
                }
            } finally {
                isInitial
                    ? setLoadingSocialDocuments(false)
                    : setLoadingMore(false)
            }
        },
        [task]
    )

    const handleInfiniteScroll = useCallback(async () => {
        if (loadingMore || !hasMore) return

        const scrollTop = document.documentElement.scrollTop
        const windowHeight = window.innerHeight
        const scrollHeight = document.documentElement.scrollHeight

        if (scrollTop + windowHeight >= scrollHeight - 1) {
            setPage((page) => page + 1)
        }
    }, [loadingMore, hasMore])

    useEffect(() => {
        if (page === 1) return
        fetchSocialDocuments(page, false)
    }, [page, fetchSocialDocuments])

    useEffect(() => {
        window.addEventListener('scroll', handleInfiniteScroll, true)

        return () => {
            window.removeEventListener('scroll', handleInfiniteScroll, true)
        }
    }, [handleInfiniteScroll])

    useEffect(() => {
        fetchSocialDocuments(1, true)
    }, [fetchSocialDocuments])

    const filteredSocialDocuments = useMemo(() => {
        if (search.trim().length === 0) {
            return socialDocuments
        }

        return socialDocuments.filter((socialDocument) =>
            socialDocument.filename?.includes(search)
        )
    }, [socialDocuments, search])

    const handleSearch = (newSearch: string) => {
        setSearch(newSearch)
    }

    const handleAddNewDocument = () => {
        setShowSaveModal(true)
    }

    const handleDocumentClick = async (socialDocument: SocialDocument) => {
        try {
            setShowSaveModal(true)
            setLoadingDetail(true)
            const detailedSocialDocument = await task.getSocialDocument(
                socialDocument.id
            )
            setSelectedSocialDocument(detailedSocialDocument)
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message)
                setShowSaveModal(false)
                setSelectedSocialDocument(null)
            }
        } finally {
            setLoadingDetail(false)
        }
    }

    const handleViewFile = () => {
        if (selectedSocialDocument) {
            task.viewSocialDocumentFile(selectedSocialDocument)
        }
    }

    const handleDownloadFile = () => {
        if (selectedSocialDocument) {
            task.downloadSocialDocumentFile(selectedSocialDocument)
        }
    }

    const handleCancelSaveModal = () => {
        setShowSaveModal(false)
        setSelectedSocialDocument(null)
    }

    const handleConfirmSaveModal = async (socialDocument: SocialDocument) => {
        try {
            setSaveSuccess(false)
            setDeleteSuccess(false)
            setShowSaveModal(false)
            setLoadingSocialDocuments(true)
            setError(null)
            const newSocialDocument = await task.saveSocialDocument(
                socialDocument
            )
            if (!socialDocument.id) {
                setSocialDocuments((prevSocialDocuments) => [
                    newSocialDocument,
                    ...prevSocialDocuments,
                ])
            }
            setSaveSuccess(true)
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message)
            }
        } finally {
            setLoadingSocialDocuments(false)
            setSelectedSocialDocument(null)
        }
    }

    const handleOpenDeleteModal = (socialDocumentId: SocialDocument['id']) => {
        setSocialDocumentIdToDelete(socialDocumentId)
        setShowDeleteModal(true)
    }

    const handleCancelDeleteModal = () => {
        setSocialDocumentIdToDelete(null)
        setShowDeleteModal(false)
    }

    const handleConfirmDeleteModal = () => {
        setShowDeleteModal(false)
        fetchDeleteEvent()
    }

    const fetchDeleteEvent = async () => {
        if (socialDocumentIdToDelete) {
            try {
                setSaveSuccess(false)
                setDeleteSuccess(false)
                setLoadingDelete(true)
                setError(null)
                await task.deleteSocialDocument(socialDocumentIdToDelete)
                setSocialDocuments((prevSocialDocuments) =>
                    prevSocialDocuments.filter(
                        (socialDocument) =>
                            socialDocument.id !== socialDocumentIdToDelete
                    )
                )
                setDeleteSuccess(true)
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message)
                }
            } finally {
                setLoadingDelete(false)
            }
        }
    }

    const loading = useMemo(() => {
        return loadingUser || loadingSocialDocuments || loadingDelete
    }, [loadingUser, loadingSocialDocuments, loadingDelete])

    return (
        <DocumentsView
            user={user}
            loading={loading}
            error={error}
            shareOptions={shareOptions}
            loadingShareOptions={loadingShareOptions}
            socialDocuments={filteredSocialDocuments}
            search={search}
            loadingMore={loadingMore}
            showSaveModal={showSaveModal}
            showDeleteModal={showDeleteModal}
            loadingDetail={loadingDetail}
            selectedSocialDocument={selectedSocialDocument}
            saveSuccess={saveSuccess}
            deleteSuccess={deleteSuccess}
            onSearchChange={handleSearch}
            onAddNewDocument={handleAddNewDocument}
            onDocumentClick={handleDocumentClick}
            onViewFile={handleViewFile}
            onDownloadFile={handleDownloadFile}
            onCancelSaveModal={handleCancelSaveModal}
            onConfirmSaveModal={handleConfirmSaveModal}
            onOpenDeleteModal={handleOpenDeleteModal}
            onCancelDeleteModal={handleCancelDeleteModal}
            onConfirmDeleteModal={handleConfirmDeleteModal}
        />
    )
}

export default DocumentsContainer
